import { Image, Tag, Typography } from 'antd';
import { useSerialTrackingEnabled } from '@/hooks/useGeneralConfig';
import { POS_IMAGE_FALLBACK } from '../utils/imagePlaceholder';
import type { PosVariantSearchItem } from '../types';

interface Props {
  item: PosVariantSearchItem;
  onClick: () => void;
}

type TagColor = 'blue' | 'red' | 'green' | 'orange' | 'purple' | 'default';

function trackingTag(mode: string | undefined): { color: TagColor; label: string } {
  if (mode === 'serial') return { color: 'blue', label: 'Serial' };
  if (mode === 'quantity') return { color: 'red', label: 'Số lượng' };
  return { color: 'default', label: '-' };
}

function pricingTag(price?: number): { color: TagColor; label: string } {
  if (price != null) return { color: 'green', label: `Giá: ${price.toLocaleString('vi-VN')}₫` };
  return { color: 'default', label: 'Giá: -' };
}

export function QRSuggestItem({ item, onClick }: Props) {
  // Setting tắt → không để lộ khái niệm serial ở dropdown gợi ý.
  const { enabled: serialTrackingEnabled } = useSerialTrackingEnabled();
  const qrCode = item.serial
    ? `${item.variant.variant_code}_${item.serial}`
    : item.variant.variant_code;
      const rawWeight = item.variant.attrs.weight_g?.attribute_value;
  const hasWeight = rawWeight != null && rawWeight !== '' && rawWeight !== 'null';

  const isSerial = !!item.serial && serialTrackingEnabled;

  const channels = [
    item.variant.is_sellable_pos && 'POS',
    item.variant.is_sellable_web && 'WEB',
    item.variant.is_sellable_app && 'APP',
  ].filter(Boolean) as string[];

  const t = trackingTag(item.product.tracking_mode);
  const p = pricingTag(item.variant.unit_price);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        padding: 12,
        cursor: 'pointer',
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        gap: 12,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = '#fafafa';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.background = '';
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <Image
          src={item.product.image || ''}
          alt={item.product.name}
          width={60}
          height={60}
          preview={false}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          fallback={POS_IMAGE_FALLBACK}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography.Text strong>{item.product.name}</Typography.Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4, marginBottom: 4 }}>
          {serialTrackingEnabled && <Tag color={t.color}>{t.label}</Tag>}
          <Tag color={p.color}>{p.label}</Tag>
          <Tag color={item.variant.is_sellable ? 'green' : 'default'}>
            Kênh: {channels.length > 0 ? channels.join(', ') : '-'}
          </Tag>
          <Tag color={item.variant.is_published != null ? 'green' : 'default'}>
            {item.variant.is_published != null ? 'Public' : '-'}
          </Tag>
        </div>
        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
          QR Code: {qrCode}
        </Typography.Text>
        {item.variant.variant_name && (
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            Tên biến thể: {item.variant.variant_name}
          </Typography.Text>
        )}
        {item.variant.barcode && (
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            Barcode: {item.variant.barcode}
          </Typography.Text>
        )}
        {isSerial ? (
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            Serial: {item.serial}
          </Typography.Text>
        ) : (
          item.stock_quantity !== null && (
            <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
              Số lượng tồn: {item.stock_quantity}
            </Typography.Text>
          )
        )}
        {hasWeight && (
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
            Khối lượng: ~{rawWeight}g
          </Typography.Text>
        )}
      </div>
    </div>
  );
}
